<template>
  <div :class="prefixUploadClass">
    <div
      :class="classes"
      @click="$_onUploaderClick"
      @drop.prevent="$_onUploaderDrop"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false">
      <input ref="input"
             :multiple="multiple"
             :accept="accept"
             type="file"
             style="display:none"
             @change="$_onUploaderChange">
      <slot></slot>
    </div>
    <slot name="tip"></slot>
    <t-upload-list
      v-if="showUploadList"
      :files="fileList"
      @on-file-remove="$_onUploadListFileRemove"
      @on-file-preview="$_onUploadListFilePreview"></t-upload-list>
  </div>
</template>
<script>
import { PREFIX_CLASS } from '../../utils/constants'
import TUploadList from './upload-list.vue'
import ajax from './ajax'

const PREFIX_UPLOAD_CLASS = `${PREFIX_CLASS}upload`

export default {
  name: 'TUpload',
  components: { TUploadList },
  props: {
    action: {
      type: String,
      required: true
    },
    headers: {
      type: Object,
      default() {
        return {}
      }
    },
    multiple: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object
    },
    disabled: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      default: 'file'
    },
    withCredentials: {
      type: Boolean,
      default: false
    },
    showUploadList: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      validator(value) {
        let validList = ['select', 'drag']
        for (let i = 0; i < validList.length; i++) {
          if (value === validList[i]) {
            return true
          }
        }
        return false
      },
      default: 'select'
    },
    method: {
      type: String,
      validator(value) {
        let validList = ['PUT', 'POST', 'GET']
        for (let i = 0; i < validList.length; i++) {
          if (value === validList[i]) {
            return true
          }
        }
        return false
      },
      default: 'POST'
    },
    format: {
      type: Array,
      default() {
        return []
      }
    },
    accept: {
      type: String
    },
    maxSize: {
      type: Number
    },
    beforeUploadClick: Function,
    beforeUpload: Function,
    onProgress: {
      type: Function,
      default() {
        return {}
      }
    },
    onSuccess: {
      type: Function,
      default() {
        return {}
      }
    },
    onError: {
      type: Function,
      default() {
        return {}
      }
    },
    onRemove: {
      type: Function,
      default() {
        return {}
      }
    },
    onPreview: {
      type: Function,
      default() {
        return {}
      }
    },
    onExceededSize: {
      type: Function,
      default() {
        return {}
      }
    },
    onFormatError: {
      type: Function,
      default() {
        return {}
      }
    },
    defaultFileList: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      prefixUploadClass: PREFIX_UPLOAD_CLASS,
      dragOver: false,
      fileList: () => [],
      tempIndex: 1
    }
  },
  computed: {
    classes() {
      return [
        {
          [`${PREFIX_UPLOAD_CLASS}-select`]: this.type === 'select',
          [`${PREFIX_UPLOAD_CLASS}--drag`]: this.type === 'drag',
          [`${PREFIX_UPLOAD_CLASS}--drag__over`]:
            this.type === 'drag' && this.dragOver
        }
      ]
    }
  },
  watch: {
    defaultFileList: {
      immediate: true,
      handler(fileList) {
        this.fileList = fileList.map(item => {
          item.status = 'finished'
          item.percentage = 100
          item.uid = Date.now() + this.tempIndex++
          return item
        })
      }
    }
  },
  methods: {
    $_onUploaderClick() {
      if (!this.disabled) {
        if (this.beforeUploadClick) {
          let before = this.beforeUploadClick()
          if (before && before.then) {
            this.$refs.input.click()
          }
        } else {
          this.$refs.input.click()
        }
      }
    },
    $_onUploaderChange(e) {
      let files = e.target.files
      if (!files) {
        return
      }
      this.$_uploadFiles(files)
      this.$refs.input.value = null
    },
    $_onUploaderDrop(e) {
      this.dragOver = false
      this.$_uploadFiles(e.dataTransfer.files)
    },
    $_uploadFiles(files) {
      let postFiles = Array.prototype.slice.call(files)
      if (!this.multiple) postFiles = postFiles.slice(0, 1)
      if (postFiles.length === 0) return
      postFiles.forEach(file => {
        this.$_upload(file)
      })
    },
    $_upload(file) {
      if (!this.beforeUpload) {
        return this.$_post(file)
      }
      let before = this.beforeUpload(file)
      if (before && before.then) {
        before.then(processedFile => {
          if (
            Object.prototype.toString.call(processedFile) === '[object File]'
          ) {
            this.$_post(processedFile)
          } else {
            this.$_post(file)
          }
          return true
        }).catch(() => {
          return new Error('Upload failure')
        })
      } else if (before !== false) {
        this.$_post(file)
      } else {
        // this.$emit('cancel', file);
      }
    },
    $_post(file) {
      // check format
      if (this.format.length) {
        const fileFormat = file.name
          .split('.')
          .pop()
          .toLocaleLowerCase()
        const checked = this.format.some(
          item => item.toLocaleLowerCase() === fileFormat
        )
        if (!checked) {
          this.onFormatError(file, this.fileList.concat())
          return false
        }
      }
      // check maxSize
      if (this.maxSize) {
        if (file.size > this.maxSize * 1024) {
          this.onExceededSize(file, this.fileList.concat())
          return false
        }
      }
      this.$_handleStart(file)
      let formData = new FormData()
      formData.append(this.name, file)
      ajax({
        headers: this.headers,
        withCredentials: this.withCredentials,
        file: file,
        data: this.data,
        filename: this.name,
        action: this.action,
        method: this.method,
        onProgress: e => {
          this.$_handleProgress(e, file)
        },
        onSuccess: res => {
          this.$_handleSuccess(res, file)
        },
        onError: (err, response) => {
          this.$_handleError(err, response, file)
        }
      })
    },
    $_handleStart(file) {
      file.uid = Date.now() + this.tempIndex++
      const _file = {
        status: 'uploading',
        name: file.name,
        size: file.size,
        percentage: 0,
        uid: file.uid,
        showProgress: true
      }
      this.fileList.push(_file)
    },
    $_getFile(file) {
      const fileList = this.fileList
      let target
      fileList.every(item => {
        target = file.uid === item.uid ? item : null
        return !target
      })
      return target
    },
    $_handleProgress(e, file) {
      const _file = this.$_getFile(file)
      this.onProgress(e, _file, this.fileList.concat())
      _file.percentage = e.percent || 0
    },
    $_handleSuccess(res, file) {
      const _file = this.$_getFile(file)
      if (_file) {
        _file.status = 'finished'
        _file.response = res
        this.onSuccess(res, _file, this.fileList.concat())
        setTimeout(() => {
          _file.showProgress = false
        }, 1000)
      }
    },
    $_handleError(err, response, file) {
      const f = this.$_getFile(file)
      f.status = 'fail'
      this.fileList.splice(this.fileList.indexOf(f), 1)
      this.onError(err, response, file)
    },
    $_onUploadListFileRemove(file) {
      this.removeFile(file)
    },
    $_onUploadListFilePreview(file) {
      if (file.status === 'finished') {
        this.onPreview(file)
      }
    },
    getFileList() {
      return this.fileList.concat()
    },
    removeFile(file) {
      this.fileList.splice(this.fileList.indexOf(file), 1)
      this.onRemove(file, this.fileList.concat())
    },
    clearFiles() {
      this.fileList = []
    }
  }
}
</script>



// WEBPACK FOOTER //
// upload.vue?6ae887ea